import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";

/**
 * DuplicatesHeader — back button + page title + summary line.
 *
 * The subtitle switches between four states: loading, empty, active-only,
 * and active + dismissed (species mode only). Kept as a switch here so
 * the copy logic lives in one obvious place instead of being spread
 * through the parent's JSX.
 */
export default function DuplicatesHeader({
  mode,
  loading,
  activeGroupCount,
  dismissedGroupCount,
  totalDupes,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const subtitle = (() => {
    if (loading) return t("duplicates.loading");
    if (activeGroupCount === 0 && dismissedGroupCount === 0) {
      return mode === "species" ? t("duplicates.speciesEmpty") : t("duplicates.empty");
    }
    if (mode === "species" && dismissedGroupCount > 0) {
      return t("duplicates.summaryWithDismissed", {
        groups: activeGroupCount,
        extras: totalDupes,
        dismissed: dismissedGroupCount,
      });
    }
    return t("duplicates.summary", { groups: activeGroupCount, extras: totalDupes });
  })();

  return (
    <>
      <div className="relative flex items-center gap-3 px-5 pt-4">
        <button
          onClick={() => navigate("/gallery")}
          aria-label={t("common.back")}
          data-testid="duplicates-back"
          className="w-9 h-9 rounded-full bg-parchment flex items-center justify-center text-leaf-ink"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <h1 className="font-display text-2xl text-leaf-ink" data-testid="duplicates-title">
          {mode === "species" ? t("duplicates.speciesTitle") : t("duplicates.title")}
        </h1>
      </div>

      <p className="mt-2 px-5 text-sm text-leaf-mist" data-testid="duplicates-subtitle">
        {subtitle}
      </p>

      {loading && (
        <div className="mt-10 flex justify-center" role="status" aria-live="polite">
          <Loader2 className="w-6 h-6 animate-spin text-leaf" strokeWidth={1.5} aria-hidden="true" />
          <span className="sr-only">{t("duplicates.loadingAria")}</span>
        </div>
      )}
    </>
  );
}
